/**
 * Разрешение текстов и ограничений поля.
 *
 * Порядок источников:
 *   1. словарь перевода (`model-docs/<lang>.json`, затем `en.json`);
 *   2. то, что прислал бэкенд портала в `FieldSpec` — он подтягивает подписи и
 *      описания из `airtable_ml_data` основного бэка;
 *   3. имя поля из node definition, приведённое к человекочитаемому виду.
 */
import type { FieldDoc } from "../../i18n/I18nProvider";
import type { EnumOption, FieldSpec } from "../../api/types";

export interface ResolvedField {
  label: string;
  description?: string;
  /** Опции, суженные до разрешённых витриной, с человекочитаемыми подписями. */
  options: { label: string; value: string }[];
  /** Опции, которые витрина скрыла — показываем в справочнике как недоступные. */
  hiddenOptions: { label: string; value: string }[];
}

const optionLabel = (option: EnumOption, doc: FieldDoc): string => {
  const raw = String(option.value);
  return doc.options?.[raw] ?? option.label ?? raw;
};

export function resolveField(field: FieldSpec, doc: FieldDoc): ResolvedField {
  const allowed = field.allowed_values?.map(String) ?? doc.allowedValues;
  const all = (field.options ?? []).map((option) => ({
    label: optionLabel(option, doc),
    value: String(option.value),
  }));

  const isAllowed = (value: string) => !allowed || allowed.includes(value);

  return {
    label: doc.label ?? field.label,
    description: doc.description ?? field.description ?? undefined,
    options: all.filter((option) => isAllowed(option.value)),
    hiddenOptions: all.filter((option) => !isAllowed(option.value)),
  };
}
